import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Edit, History, TrendingUp } from '@mui/icons-material';
import { apiService } from '../services/api';
import { displayName } from '../types';

interface LeaveBalance {
  id: string;
  leaveType: {
    id: string;
    name: string;
    code: string;
  };
  year: number;
  openingBalanceDays: number;
  accruedDays: number;
  usedDays: number;
  adjustedDays: number;
  closingBalanceDays: number;
  lastAccrualDate?: string;
  carryOverDays?: number;
  carryOverExpiry?: string;
}

interface LeaveType {
  id: string;
  name: string;
  code: string;
  isPaid: boolean;
  requiresApproval: boolean;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
}

interface ManageLeaveBalanceModalProps {
  open: boolean;
  onClose: () => void;
  employee: Employee | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`balance-tabpanel-${index}`}
      aria-labelledby={`balance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ManageLeaveBalanceModal: React.FC<ManageLeaveBalanceModalProps> = ({
  open,
  onClose,
  employee,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [initializingBalances, setInitializingBalances] = useState(false);
  const [applyingPolicy, setApplyingPolicy] = useState(false);
  
  // Manual adjustment form
  const [adjustmentForm, setAdjustmentForm] = useState({
    leaveTypeId: '',
    adjustmentDays: 0,
    reason: '',
  });

  // Initialize balances form
  const [initializeForm, setInitializeForm] = useState({
    showCustomForm: false,
    balances: {} as Record<string, number>, // leaveTypeId -> opening balance days
  });

  useEffect(() => {
    if (open && employee) {
      fetchData();
    }
  }, [open, employee, selectedYear]);

  const fetchData = async () => {
    if (!employee) return;
    
    try {
      setLoading(true);
      setError('');
      const [balancesData, typesData] = await Promise.all([
        apiService.getEmployeeLeaveBalances(employee.id, selectedYear),
        apiService.getLeaveTypes(),
      ]);
      
      setLeaveBalances(balancesData || []);
      setLeaveTypes(typesData || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeBalances = async (customBalances?: Record<string, number>) => {
    if (!employee) return;

    try {
      setInitializingBalances(true);
      setError('');
      
      await apiService.initializeEmployeeLeaveBalances(employee.id, selectedYear, customBalances);
      
      await fetchData();
      setInitializeForm({ showCustomForm: false, balances: {} });
      setError('');
    } catch (err: any) {
      console.error('Error initializing balances:', err);
      setError(err.response?.data?.message || 'Failed to initialize leave balances');
    } finally {
      setInitializingBalances(false);
    }
  };

  const handleShowCustomInitialize = () => {
    // Initialize the form with leave types
    const initialBalances: Record<string, number> = {};
    leaveTypes.forEach(type => {
      initialBalances[type.id] = 0;
    });
    setInitializeForm({
      showCustomForm: true,
      balances: initialBalances,
    });
  };

  const handleCustomInitializeSubmit = () => {
    handleInitializeBalances(initializeForm.balances);
  };

  const handleApplyPolicy = async () => {
    if (!employee) return;
    try {
      setApplyingPolicy(true);
      setError('');
      await apiService.applyLeavePolicyToEmployee(employee.id, selectedYear);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply leave policy');
    } finally {
      setApplyingPolicy(false);
    }
  };

  const handleManualAdjustment = async () => {
    if (!employee || !adjustmentForm.leaveTypeId || !adjustmentForm.reason) {
      setError('Please fill in all adjustment fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await apiService.adjustEmployeeLeaveBalance(
        employee.id, 
        adjustmentForm.leaveTypeId, 
        adjustmentForm.adjustmentDays, 
        adjustmentForm.reason,
        selectedYear
      );
      
      // Reset form
      setAdjustmentForm({ leaveTypeId: '', adjustmentDays: 0, reason: '' });
      await fetchData();
      setTabValue(0); // Switch back to overview tab
    } catch (err: any) {
      console.error('Error making adjustment:', err);
      setError(err.response?.data?.message || 'Failed to make adjustment');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({length: 5}, (_, i) => currentYear - 2 + i);

  const selectedLeaveType = leaveTypes.find(lt => lt.id === adjustmentForm.leaveTypeId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Manage Leave Balances - {displayName(employee)}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Year Selection */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              fullWidth
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              SelectProps={{ native: true }}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 9 }}>
            <Box display="flex" gap={1} height="100%" alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                onClick={handleApplyPolicy}
                disabled={applyingPolicy || initializingBalances || loading}
                color="success"
                size="small"
                title="Apply tenure-based policy: 14 days base + 1/year of service, max 20 days"
              >
                {applyingPolicy ? <CircularProgress size={18} /> : 'Apply Policy'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleShowCustomInitialize}
                disabled={initializingBalances || loading || leaveTypes.length === 0}
                color="primary"
                size="small"
              >
                Custom Initialize
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab icon={<TrendingUp />} label="Current Balances" />
            <Tab icon={<Edit />} label="Manual Adjustment" />
            <Tab icon={<History />} label="Balance History" />
          </Tabs>
        </Box>

        {/* Tab 1: Current Balances */}
        <TabPanel value={tabValue} index={0}>
          {/* Custom Initialize Form */}
          {initializeForm.showCustomForm && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Custom Initialize Leave Balances for {selectedYear}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set custom opening balance amounts for each leave type.
                </Typography>
                
                <Grid container spacing={2}>
                  {leaveTypes.map((leaveType) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={leaveType.id}>
                      <TextField
                        fullWidth
                        label={`${leaveType.name} (${leaveType.code})`}
                        type="number"
                        value={initializeForm.balances[leaveType.id] || 0}
                        onChange={(e) => {
                          setInitializeForm(prev => ({
                            ...prev,
                            balances: {
                              ...prev.balances,
                              [leaveType.id]: Number(e.target.value)
                            }
                          }));
                        }}
                        inputProps={{ step: 0.5, min: 0 }}
                        InputProps={{
                          endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>days</Typography>
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box display="flex" gap={2} mt={3}>
                  <Button
                    variant="contained"
                    onClick={handleCustomInitializeSubmit}
                    disabled={initializingBalances}
                  >
                    {initializingBalances ? <CircularProgress size={20} /> : 'Initialize Balances'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setInitializeForm({ showCustomForm: false, balances: {} })}
                    disabled={initializingBalances}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : leaveBalances.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" gutterBottom>
                No leave balances found for {selectedYear}.
              </Typography>
              <Button
                variant="contained"
                onClick={handleShowCustomInitialize}
                disabled={initializingBalances || leaveTypes.length === 0}
                startIcon={<Add />}
              >
                Initialize Leave Balances
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {leaveBalances.map((balance) => (
                <Grid size={{ xs: 12, md: 6 }} key={balance.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                          {balance.leaveType.name}
                        </Typography>
                        <Chip 
                          label={balance.leaveType.code} 
                          size="small" 
                          color={balance.closingBalanceDays > 0 ? 'success' : 'default'}
                        />
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">
                            Opening Balance
                          </Typography>
                          <Typography variant="h6">
                            {balance.openingBalanceDays} days
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">
                            Accrued
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            +{balance.accruedDays} days
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">
                            Used
                          </Typography>
                          <Typography variant="h6" color="warning.main">
                            -{balance.usedDays} days
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">
                            Adjustments
                          </Typography>
                          <Typography variant="h6" color={balance.adjustedDays >= 0 ? "success.main" : "error.main"}>
                            {balance.adjustedDays >= 0 ? '+' : ''}{balance.adjustedDays} days
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Available Balance
                        </Typography>
                        <Typography variant="h4" color="primary">
                          {balance.closingBalanceDays} days
                        </Typography>
                      </Box>

                      {balance.lastAccrualDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Last accrual: {new Date(balance.lastAccrualDate).toLocaleDateString()}
                        </Typography>
                      )}
                      {balance.carryOverDays != null && balance.carryOverDays > 0 && (
                        <Box mt={1} px={1.5} py={1} sx={{ bgcolor: 'info.main', borderRadius: 1, opacity: 0.9 }}>
                          <Typography variant="caption" sx={{ color: '#fff', display: 'block', fontWeight: 600 }}>
                            🔄 {balance.carryOverDays} day{balance.carryOverDays !== 1 ? 's' : ''} carried over from last year
                          </Typography>
                          {balance.carryOverExpiry && (
                            <Typography variant="caption" sx={{ color: '#fff', opacity: 0.85 }}>
                              Expires {new Date(balance.carryOverExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Tab 2: Manual Adjustment */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Manual Balance Adjustment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use this to manually adjust an employee's leave balance. This will be recorded as an adjustment.
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      value={adjustmentForm.leaveTypeId}
                      onChange={(e) => setAdjustmentForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                      label="Leave Type"
                    >
                      {leaveTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name} ({type.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="Adjustment (Days)"
                    type="number"
                    value={adjustmentForm.adjustmentDays}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, adjustmentDays: Number(e.target.value) }))}
                    helperText="Use negative numbers to reduce balance"
                    inputProps={{ step: 0.5 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="e.g., Bonus leave, Correction, etc."
                    required
                  />
                </Grid>
                <Grid size={12}>
                  <Box display="flex" gap={2} alignItems="center">
                    <Button
                      variant="contained"
                      onClick={handleManualAdjustment}
                      disabled={!adjustmentForm.leaveTypeId || !adjustmentForm.reason || loading}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Apply Adjustment'}
                    </Button>
                    
                    {selectedLeaveType && adjustmentForm.adjustmentDays !== 0 && (
                      <Typography variant="body2" color="text.secondary">
                        This will {adjustmentForm.adjustmentDays > 0 ? 'add' : 'subtract'} {Math.abs(adjustmentForm.adjustmentDays)} days 
                        {adjustmentForm.adjustmentDays > 0 ? ' to ' : ' from '} 
                        {selectedLeaveType.name} balance
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 3: Balance History */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Balance History for {selectedYear}
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Leave Type</TableCell>
                      <TableCell align="right">Opening</TableCell>
                      <TableCell align="right">Accrued</TableCell>
                      <TableCell align="right">Used</TableCell>
                      <TableCell align="right">Adjusted</TableCell>
                      <TableCell align="right">Current</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaveBalances.map((balance) => (
                      <TableRow key={balance.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip label={balance.leaveType.code} size="small" />
                            {balance.leaveType.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{balance.openingBalanceDays}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          +{balance.accruedDays}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'warning.main' }}>
                          -{balance.usedDays}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ color: balance.adjustedDays >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {balance.adjustedDays >= 0 ? '+' : ''}{balance.adjustedDays}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{balance.closingBalanceDays} days</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {leaveBalances.length === 0 && (
                <Box textAlign="center" py={3}>
                  <Typography color="text.secondary">
                    No balance history available for {selectedYear}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageLeaveBalanceModal;